import React, { useEffect, useId, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import SaveAsOutlinedIcon from '@mui/icons-material/SaveAsOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import { useReportAction } from '~/pages/LeLens/useReportAction';

import {
  MAX_PERSONAL_VIEW_NAME_LENGTH,
  type PersonalView,
  type PersonalViewState,
  personalViewFailure,
  useCreatePersonalView,
  useGetPersonalViews,
  useUpdatePersonalView,
} from '~/hooks/apis/reporting/usePersonalViews';
import useCurrentUser from '~/hooks/useCurrentUser';

export interface PersonalViewsProps {
  lensId: string;
  reportVersion: number;
  /** Capture the current view on demand. Throw if its state cannot be safely represented. */
  state: () => PersonalViewState;
  onOpen: (state: PersonalViewState) => void;
  onReset: () => void;
  onCopy: () => Promise<void>;
  /** With onOpenChange the parent owns the dialog and no trigger row is rendered. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

type ViewAction = { type: 'rename' | 'delete' | 'open' | 'replace'; view: PersonalView };
type Feedback = { severity: 'success' | 'error' | 'warning'; message: string };

function validViewState(state: PersonalViewState): boolean {
  return state?.version === 1 && typeof state.search === 'string';
}

function nameError(name: string): string | null {
  if (!name.trim()) return 'Enter a name for this view.';
  if (Array.from(name).some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) {
    return 'Use a name without control characters.';
  }
  if (name.trim().length > MAX_PERSONAL_VIEW_NAME_LENGTH) {
    return `Use ${MAX_PERSONAL_VIEW_NAME_LENGTH} characters or fewer.`;
  }
  return null;
}

/** Account-private shortcuts shared by generic Lens reports and native reporting pages. */
export default function PersonalViews({
  lensId,
  reportVersion,
  state,
  onOpen,
  onReset,
  onCopy,
  open: controlledOpen,
  onOpenChange,
}: PersonalViewsProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const dialogId = useId();
  const { isAuthenticated, user } = useCurrentUser();
  const query = useGetPersonalViews(lensId);
  const createView = useCreatePersonalView(lensId);
  const updateView = useUpdatePersonalView(lensId);
  const [ownOpen, setOwnOpen] = useState(false);
  const controlled = Boolean(onOpenChange);
  const open = controlled ? Boolean(controlledOpen) : ownOpen;
  const setOpen = (next: boolean) => (onOpenChange ? onOpenChange(next) : setOwnOpen(next));
  const [name, setName] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [action, setAction] = useState<ViewAction | null>(null);
  const [renamed, setRenamed] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [copying, setCopying] = useState(false);
  const actorEmail = user?.email?.trim().toLowerCase();
  const signedIn = Boolean(isAuthenticated && user?.email);
  // A successful read confirms that the companion API is available for this account/report.
  const canManage = signedIn && query.isSuccess;
  const busy = createView.isPending || updateView.isPending;
  const readableViews = query.isSuccess ? query.data.views : [];
  const activeViews = readableViews.filter((view) => !view.deletedAt);
  const deletedViews = readableViews.filter((view) => Boolean(view.deletedAt));
  const currentReportVersion = query.data?.reportVersion ?? reportVersion;

  useEffect(() => {
    setOpen(false);
    setName('');
    setMakeDefault(false);
    setShowDeleted(false);
    setAction(null);
    setActionError(null);
    setFeedback(null);
  }, [lensId, user?.email]);

  useReportAction('saved-views', () => setOpen(true), !query.isPending);

  const close = () => {
    if (!busy) {
      setOpen(false);
      setAction(null);
      setFeedback(null);
    }
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || nameError(name) || !canManage) return;
    setFeedback(null);
    let captured: PersonalViewState;
    try {
      captured = state();
      if (!validViewState(captured)) throw new Error('This view cannot be saved. Reset the report and try again.');
    } catch (error) {
      setFeedback({
        severity: 'warning',
        message: personalViewFailure(error, 'This view cannot be saved. Reset the report and try again.'),
      });
      return;
    }
    try {
      await createView.mutateAsync({ name: name.trim(), state: captured, isDefault: makeDefault });
      setName('');
      setMakeDefault(false);
    } catch (error) {
      setFeedback({ severity: 'error', message: personalViewFailure(error, 'Couldn’t save this view. Try again.') });
    }
  };

  const openView = (view: PersonalView) => {
    try {
      if (!validViewState(view.state)) {
        throw new Error('This saved view uses an unsupported format. Save a new view from the current report.');
      }
      onOpen(view.state);
      setAction(null);
      setOpen(false);
      setFeedback(null);
    } catch (error) {
      const message = personalViewFailure(error, 'Couldn’t open this view. Try again.');
      if (action) setActionError(message);
      else setFeedback({ severity: 'error', message });
    }
  };

  const selectAction = (next: ViewAction) => {
    setAction(next);
    setActionError(null);
    setRenamed(next.view.name);
  };

  const update = async (input: {
    id: string;
    name?: string;
    state?: PersonalViewState;
    isDefault?: boolean;
    deleted?: boolean;
  }) => {
    setFeedback(null);
    setActionError(null);
    try {
      await updateView.mutateAsync(input);
      setAction(null);
    } catch (error) {
      const failure = personalViewFailure(error, 'Couldn’t update this view. Try again.');
      if (action) setActionError(failure);
      else setFeedback({ severity: 'error', message: failure });
    }
  };

  const copy = async () => {
    if (copying) return;
    setCopying(true);
    setFeedback(null);
    try {
      await onCopy();
      setFeedback({ severity: 'success', message: 'Link copied.' });
    } catch (error) {
      setFeedback({
        severity: 'error',
        message: personalViewFailure(error, 'Couldn’t copy the view link. Try again.'),
      });
    } finally {
      setCopying(false);
    }
  };

  const reset = () => {
    try {
      onReset();
      close();
    } catch (error) {
      setFeedback({ severity: 'error', message: personalViewFailure(error, 'Couldn’t reset the view. Try again.') });
    }
  };

  return (
    <>
      {!controlled && (
        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.5} alignItems="center">
          <Button
            aria-haspopup="dialog"
            onClick={() => {
              setFeedback(null);
              setOpen(true);
            }}
            size="small"
            startIcon={<BookmarkBorderOutlinedIcon />}
            sx={{ color: tokens.accent }}
            variant="text"
          >
            Saved views
          </Button>
          <Button
            disabled={copying}
            onClick={() => void copy()}
            size="small"
            startIcon={copying ? <CircularProgress color="inherit" size={16} /> : <LinkOutlinedIcon />}
            sx={{ color: tokens.accent }}
            variant="text"
          >
            {copying ? 'Copying…' : 'Copy exact view link'}
          </Button>
          {!open && feedback && (
            <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ width: '100%' }}>
              {feedback.message}
            </Alert>
          )}
        </Stack>
      )}

      <Dialog aria-labelledby={dialogId} fullWidth maxWidth="sm" onClose={close} open={open}>
        <DialogTitle id={dialogId}>Saved views</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5}>
            {!signedIn && <Alert severity="info">Sign in to save and manage your views.</Alert>}
            {feedback && (
              <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
                {feedback.message}
              </Alert>
            )}
            {query.isError && (
              <Alert
                severity="error"
                action={
                  <Button disabled={query.isFetching} onClick={() => void query.refetch()} size="small">
                    Retry
                  </Button>
                }
              >
                {[404, 501].includes((query.error as { status?: number }).status ?? 0)
                  ? 'Saved views are not available in this environment yet.'
                  : personalViewFailure(query.error, 'Couldn’t load your saved views.')}
              </Alert>
            )}
            {updateView.isPending && <LinearProgress aria-label="Updating saved views" />}

            <Box component="form" onSubmit={(event) => void save(event)}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  disabled={busy || !canManage}
                  fullWidth
                  helperText={name ? nameError(name) : undefined}
                  error={Boolean(name && nameError(name))}
                  inputProps={{ maxLength: MAX_PERSONAL_VIEW_NAME_LENGTH }}
                  label="Name"
                  onChange={(event) => setName(event.target.value)}
                  size="small"
                  value={name}
                />
                <Tooltip title="Make default">
                  <span>
                    <IconButton
                      aria-label="Make default"
                      aria-pressed={makeDefault}
                      disabled={busy || !canManage}
                      onClick={() => setMakeDefault(!makeDefault)}
                      sx={{ height: 40, width: 40 }}
                    >
                      {makeDefault ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  disabled={busy || !canManage || Boolean(nameError(name))}
                  sx={{ flexShrink: 0, height: 40 }}
                  type="submit"
                  variant="contained"
                >
                  {createView.isPending ? 'Saving…' : 'Save view'}
                </Button>
              </Stack>
            </Box>

            <Stack>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {query.data?.canManageAll ? 'All saved views' : 'Your views'}
              </Typography>
              {query.isLoading && (
                <Stack direction="row" spacing={1} alignItems="center" role="status" sx={{ py: 1 }}>
                  <CircularProgress color="inherit" size={18} />
                  <Typography variant="body2">Loading your views…</Typography>
                </Stack>
              )}
              {query.isSuccess && activeViews.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No saved views yet.
                </Typography>
              )}
              {activeViews.map((view) => {
                const own = !view.ownerEmail || view.ownerEmail === actorEmail;
                const changed = view.reportChanged || view.reportVersion !== currentReportVersion;
                return (
                  <Stack
                    key={view.id}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    useFlexGap
                    sx={{ borderTop: 1, borderColor: 'divider', py: 0.5 }}
                  >
                    <Button
                      disabled={busy}
                      onClick={() => (changed ? selectAction({ type: 'open', view }) : openView(view))}
                      sx={{
                        color: 'text.primary',
                        flex: 1,
                        justifyContent: 'flex-start',
                        ml: -1,
                        minWidth: 0,
                        overflowWrap: 'anywhere',
                        textAlign: 'left',
                      }}
                    >
                      <Box component="span" sx={{ minWidth: 0 }}>
                        {view.name}
                        {query.data?.canManageAll && view.ownerEmail && (
                          <Typography component="span" display="block" variant="caption" color="text.secondary">
                            {view.ownerEmail}
                          </Typography>
                        )}
                      </Box>
                    </Button>
                    {view.isDefault && <Chip label={own ? 'Default' : 'Owner’s default'} size="small" />}
                    <Tooltip
                      title={
                        view.isDefault
                          ? own
                            ? 'Remove default'
                            : 'Remove owner’s default'
                          : own
                            ? 'Make default'
                            : 'Make owner’s default'
                      }
                    >
                      <span>
                        <IconButton
                          aria-label={
                            view.isDefault
                              ? `Remove ${view.name} as ${own ? 'default' : 'owner’s default'}`
                              : `Make ${view.name} ${own ? 'default' : 'owner’s default'}`
                          }
                          aria-pressed={view.isDefault}
                          disabled={busy}
                          onClick={() => void update({ id: view.id, isDefault: !view.isDefault })}
                          size="small"
                        >
                          {view.isDefault ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Rename">
                      <span>
                        <IconButton
                          aria-label={`Rename ${view.name}`}
                          disabled={busy}
                          onClick={() => selectAction({ type: 'rename', view })}
                          size="small"
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Replace with current settings">
                      <span>
                        <IconButton
                          aria-label={`Replace ${view.name} with current settings`}
                          disabled={busy}
                          onClick={() => selectAction({ type: 'replace', view })}
                          size="small"
                        >
                          <SaveAsOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          aria-label={`Delete ${view.name}`}
                          disabled={busy}
                          onClick={() => selectAction({ type: 'delete', view })}
                          size="small"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                );
              })}
            </Stack>

            {deletedViews.length > 0 && (
              <Stack spacing={1}>
                <Box>
                  <Button aria-expanded={showDeleted} onClick={() => setShowDeleted(!showDeleted)} size="small">
                    {showDeleted ? 'Hide deleted views' : `Deleted views (${deletedViews.length})`}
                  </Button>
                </Box>
                {showDeleted &&
                  deletedViews.map((view) => (
                    <Stack key={view.id} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                        {view.name}
                        {query.data?.canManageAll && view.ownerEmail ? ` · ${view.ownerEmail}` : ''}
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        <Button disabled={busy} onClick={() => selectAction({ type: 'rename', view })} size="small">
                          Rename
                        </Button>
                        <Button
                          disabled={busy}
                          onClick={() => void update({ id: view.id, deleted: false })}
                          size="small"
                        >
                          {updateView.isPending && updateView.variables?.id === view.id ? 'Restoring…' : 'Restore'}
                        </Button>
                      </Stack>
                    </Stack>
                  ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Button disabled={busy} onClick={reset}>
            Reset to published view
          </Button>
          <Button disabled={busy} onClick={close}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        aria-labelledby={`${dialogId}-action`}
        fullWidth
        maxWidth="xs"
        onClose={busy ? undefined : () => setAction(null)}
        open={Boolean(action)}
      >
        <DialogTitle id={`${dialogId}-action`} sx={{ overflowWrap: 'anywhere' }}>
          {action?.type === 'rename'
            ? 'Rename view'
            : action?.type === 'delete'
              ? `Delete “${action.view.name}”?`
              : action?.type === 'replace'
                ? `Replace settings for “${action.view.name}”?`
                : 'The report has changed'}
        </DialogTitle>
        <DialogContent>
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}
          {action?.type === 'rename' && (
            <TextField
              autoFocus
              disabled={busy}
              error={Boolean(nameError(renamed))}
              fullWidth
              helperText={nameError(renamed) ?? `${renamed.trim().length}/${MAX_PERSONAL_VIEW_NAME_LENGTH}`}
              inputProps={{ maxLength: MAX_PERSONAL_VIEW_NAME_LENGTH }}
              label="View name"
              onChange={(event) => setRenamed(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !busy && !nameError(renamed)) {
                  event.preventDefault();
                  void update({ id: action.view.id, name: renamed.trim() });
                }
              }}
              required
              sx={{ mt: 1 }}
              value={renamed}
            />
          )}
          {action?.type === 'delete' && (
            <DialogContentText>
              You can restore it from Deleted views.{' '}
              {action.view.isDefault && 'It will also be removed as its owner’s default.'}
            </DialogContentText>
          )}
          {action?.type === 'replace' && (
            <DialogContentText>
              Save the report’s current filters, dates and tabs to this view
              {action.view.ownerEmail ? ` for ${action.view.ownerEmail}` : ''}.
            </DialogContentText>
          )}
          {action?.type === 'open' && (
            <DialogContentText sx={{ overflowWrap: 'anywhere' }}>
              “{action.view.name}” was saved before the report changed, so some filters may not apply.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setAction(null)}>
            Cancel
          </Button>
          <Button
            disabled={busy || (action?.type === 'rename' && Boolean(nameError(renamed)))}
            onClick={() => {
              if (action?.type === 'replace') {
                try {
                  const captured = state();
                  if (!validViewState(captured))
                    throw new Error('This view cannot be saved. Reset the report and try again.');
                  void update({ id: action.view.id, state: captured });
                } catch (error) {
                  setActionError(personalViewFailure(error, 'Couldn’t capture the current settings.'));
                }
              }
              if (action?.type === 'open') openView(action.view);
              if (action?.type === 'rename') void update({ id: action.view.id, name: renamed.trim() });
              if (action?.type === 'delete') void update({ id: action.view.id, deleted: true });
            }}
            variant="contained"
          >
            {busy
              ? 'Saving…'
              : action?.type === 'rename'
                ? 'Save name'
                : action?.type === 'delete'
                  ? 'Delete view'
                  : action?.type === 'replace'
                    ? 'Replace settings'
                    : 'Open anyway'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
